import { isEqual } from 'lodash';
import {
  AMENITY_SPACES_CATEGORY,
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  SUPPORT_SPACES_CATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import {
  BenchmarkReportCardType,
  BenchmarkReportCategoriesMap,
  BenchmarkReportIcon,
  BenchmarkReportIconsMap,
  BenchmarkReportIconType,
} from 'spw-redux/modules/benchmark-reports/types';

export const PRESET_ICONS: {
  cardType: BenchmarkReportCardType;
  cardTitle?: string;
  shouldShowColorDot?: boolean;
  getColor?: (params: {
    currentReportCategories: BenchmarkReportCategoriesMap;
  }) => string;
  sections: {
    iconType: BenchmarkReportIconType;
    category?: string;
    iconOptions: string[];
    defaultGradientIndex?: number;
  }[];
}[] = [
  {
    cardTitle: 'Individual Workspaces Analysis',
    cardType: BenchmarkReportCardType.individualWorkspacesAnalysis,
    getColor: ({ currentReportCategories }) =>
      currentReportCategories[INDIVIDUAL_WORKSPACES_CATEGORY].color,
    shouldShowColorDot: true,
    sections: [
      {
        iconType: BenchmarkReportIconType.workstations,
        category: WORKSTATIONS_SUBCATEGORY,
        defaultGradientIndex: 15,
        iconOptions: [
          'custom-workstation-default',
          'hotel-desk-default',
          'meeting-chair',
          'custom-private-office-default',
          'iw-category-default',
        ],
      },
      {
        iconType: BenchmarkReportIconType.privateOffices,
        category: PRIVATE_OFFICES_SUBCATEGORY,
        defaultGradientIndex: 10,
        iconOptions: [
          'private-office-default',
          'workstation-default',
          'iw-custom-subcategory-default',
          'shared-private-office-default',
          'workstation-3',
        ],
      },
      {
        iconType: BenchmarkReportIconType.otherWorkpoints,
        category: CUSTOM_SUBCATEGORY,
        defaultGradientIndex: 6,
        iconOptions: [
          'executive-private-office-default',
          'shared-private-office-2',
          'workstation-2',
          'desk-chair',
          'iw-category-3',
        ],
      },
    ],
  },
  {
    cardTitle: 'USF Allocation By Space Category',
    cardType: BenchmarkReportCardType.usfAllocationBySpaceCategory,
    shouldShowColorDot: true,
    sections: [
      {
        iconType: BenchmarkReportIconType.individualWorkspaces,
        category: INDIVIDUAL_WORKSPACES_CATEGORY,
        iconOptions: [
          'iw-category-default',
          'iw-category-2',
          'custom-workstation-default',
          'custom-private-office-default',
          'desk-chair',
        ],
      },
      {
        iconType: BenchmarkReportIconType.meetingSpaces,
        category: MEETING_SPACES_CATEGORY,
        iconOptions: [
          'meeting-category-default',
          'multi-purpose-default',
          'open-collab-default',
          'custom-meeting-default',
          'meeting-2',
        ],
      },
      {
        iconType: BenchmarkReportIconType.amenitySpaces,
        category: AMENITY_SPACES_CATEGORY,
        iconOptions: [
          'amenity-category-default',
          'cafe-default',
          'fitness-default',
          'break-room-2',
          'pantry-default',
        ],
      },
      {
        iconType: BenchmarkReportIconType.supportSpaces,
        category: SUPPORT_SPACES_CATEGORY,
        iconOptions: [
          'support-category-default',
          'mail-room',
          'custom-support-default',
          'copy-print-default',
          'lockers-default',
        ],
      },
    ],
  },
  {
    cardTitle: 'Work Seat Ratios',
    cardType: BenchmarkReportCardType.workSeatRatios,
    shouldShowColorDot: false,
    sections: [
      {
        iconType: BenchmarkReportIconType.workSeat,
        iconOptions: [
          'meeting-chair',
          'custom-workstation-default',
          'hotel-desk-default',
          'custom-private-office-default',
          'iw-category-default',
        ],
      },
    ],
  },
  {
    cardTitle: 'USF Metrics',
    shouldShowColorDot: false,
    cardType: BenchmarkReportCardType.usfMetrics,
    sections: [
      {
        iconType: BenchmarkReportIconType.workSeat,
        iconOptions: [
          'meeting-chair',
          'custom-workstation-default',
          'hotel-desk-default',
          'custom-private-office-default',
          'iw-category-default',
        ],
      },
      {
        iconType: BenchmarkReportIconType.headcount,
        iconOptions: [
          'headcount-default',
          'headcount-2',
          'headcount-3',
          'headcount-4',
          'headcount-5',
        ],
      },
    ],
  },
];

export const DEFAULT_ICONS_MAP = PRESET_ICONS.reduce(
  (map, { cardType, sections }) => ({
    ...map,
    [cardType]: sections.reduce(
      (sectionsMap, { iconType, iconOptions }) => ({
        ...sectionsMap,
        [iconType]: iconOptions[0],
      }),
      {}
    ),
  }),
  {} as BenchmarkReportIconsMap
);

export const iconsHaveChanged = (
  currentIconsMap: BenchmarkReportIconsMap,
  initialIconsMap?: BenchmarkReportIconsMap
) => {
  const initialIconsMapIsNotEmpty =
    initialIconsMap && !!Object.keys(initialIconsMap).length;

  if (initialIconsMapIsNotEmpty)
    return !isEqual(initialIconsMap, currentIconsMap);

  return !isEqual(currentIconsMap, DEFAULT_ICONS_MAP);
};

export const parseIconsMapToList = (iconsMap: BenchmarkReportIconsMap) => {
  const icons: BenchmarkReportIcon[] = [];
  Object.keys(iconsMap).forEach((cardType: BenchmarkReportCardType) => {
    Object.keys(iconsMap[cardType]).forEach(
      (iconType: BenchmarkReportIconType) => {
        icons.push({
          cardType,
          iconType,
          iconPath: iconsMap[cardType][iconType],
        });
      }
    );
  });
  return icons;
};

export const parseIconsListToMap = (iconsList: BenchmarkReportIcon[]) => {
  return iconsList.reduce(
    (map, icon) => ({
      ...map,
      [icon.cardType]: {
        ...map[icon.cardType],
        [icon.iconType]: icon.iconPath,
      },
    }),
    {} as BenchmarkReportIconsMap
  );
};
